import { useState, useEffect } from "react";
import { 
  Menu, X, ChevronDown, Calendar, MapPin, Clock, 
  Phone, Mail, Send, Heart, Users, Bell, ArrowRight,
  Facebook, Instagram
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { toast } from "sonner";

// Assets
const ASSETS = {
  hero_goddess: "https://customer-assets.emergentagent.com/job_samaj-temple/artifacts/hpo0s500_image.png",
  temple_deity: "https://customer-assets.emergentagent.com/job_samaj-temple/artifacts/8n0dbphz_OVP09915.jpg",
  festival_1: "https://customer-assets.emergentagent.com/job_samaj-temple/artifacts/rhrhi05x_DSC_1194.jpg",
  festival_2: "https://customer-assets.emergentagent.com/job_samaj-temple/artifacts/0ceyqlqy_DSC_1209.jpg",
  procession: "https://customer-assets.emergentagent.com/job_samaj-temple/artifacts/pqauhgim_DSC_1222.jpg",
};

// Static Data
const EVENTS = [
  {
    event_id: "evt_1",
    title: "Navratri Celebration 2026",
    description: "Join us for the grand 9-day Navratri celebration with daily aarti, garba, and prasad distribution.",
    date: "2026-03-22",
    time: "6:00 PM - 10:00 PM",
    location: "Bhavsar Kshatriya Samaj Temple, Kalaburagi",
    image_url: ASSETS.festival_1,
    category: "festival",
  },
  {
    event_id: "evt_2",
    title: "Annual Goddess Procession",
    description: "The sacred annual procession of Mata Ji through Kalaburagi city with devotees.",
    date: "2026-04-15",
    time: "8:00 AM - 2:00 PM",
    location: "Starting from Bhavsar Kshatriya Samaj Temple",
    image_url: ASSETS.procession,
    category: "procession",
  },
  {
    event_id: "evt_3",
    title: "Community Gathering & Bhandara",
    description: "Monthly community gathering with free bhandara for all devotees and community members.",
    date: "2026-02-10",
    time: "11:00 AM - 3:00 PM",
    location: "Bhavsar Kshatriya Samaj Temple, Kalaburagi",
    image_url: ASSETS.festival_2,
    category: "community",
  }
];

const GALLERY = [
  { image_id: "img_1", title: "Divine Goddess - Main Shrine", image_url: ASSETS.hero_goddess, category: "goddess" },
  { image_id: "img_2", title: "Temple Deity", image_url: ASSETS.temple_deity, category: "goddess" },
  { image_id: "img_3", title: "Festival Celebration", image_url: ASSETS.festival_1, category: "events" },
  { image_id: "img_4", title: "Community Procession", image_url: ASSETS.festival_2, category: "community" },
  { image_id: "img_5", title: "Grand Procession", image_url: ASSETS.procession, category: "events" },
];

const COMMUNITY_POSTS = [
  {
    post_id: "post_1",
    title: "Temple Timing Update",
    content: "Dear devotees, the temple will remain open from 6:00 AM to 9:00 PM daily. Special aarti timings: Morning 7:00 AM, Evening 7:00 PM.",
    post_type: "announcement",
    is_pinned: true,
  },
  {
    post_id: "post_2",
    title: "Volunteer Registration Open",
    content: "We are looking for volunteers to help during the upcoming Navratri celebrations. Register at the temple office or contact us.",
    post_type: "news",
    is_pinned: false,
  }
];

const HomePageStatic = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedGalleryFilter, setSelectedGalleryFilter] = useState("all");
  const [lightboxImage, setLightboxImage] = useState(null);
  const [contactForm, setContactForm] = useState({
    name: "", email: "", phone: "", subject: "", message: ""
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleContactSubmit = (e) => {
    e.preventDefault();
    // Open WhatsApp with pre-filled message
    const message = `Name: ${contactForm.name}%0AEmail: ${contactForm.email}%0APhone: ${contactForm.phone}%0ASubject: ${contactForm.subject}%0AMessage: ${contactForm.message}`;
    window.open(`https://wa.me/919110278059?text=${message}`, '_blank');
    toast.success("Redirecting to WhatsApp...");
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMenuOpen(false);
  };

  const filteredGallery = selectedGalleryFilter === "all" 
    ? GALLERY 
    : GALLERY.filter(img => img.category === selectedGalleryFilter);

  const navLinks = [
    { label: "Home", id: "hero" },
    { label: "About Goddess", id: "about" },
    { label: "Events", id: "events" },
    { label: "Gallery", id: "gallery" },
    { label: "Donations", id: "donations" },
    { label: "Community", id: "community" },
    { label: "Contact", id: "contact" },
  ];

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Navigation */}
      <header 
        className={`fixed top-0 w-full z-40 transition-all duration-300 ${
          isScrolled ? "nav-blur border-b border-[#E6DCCA]/50 shadow-sm" : "bg-transparent"
        }`}
        data-testid="main-header"
      >
        <div className="container-custom">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <div className="flex items-center gap-3" data-testid="logo">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden border-2 border-[#D4AF37]">
                <img 
                  src={ASSETS.hero_goddess} 
                  alt="Temple Logo" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="hidden sm:block">
                <h1 className="font-playfair text-lg md:text-xl font-semibold text-[#290909]">
                  Bhavsar Kshatriya Samaj
                </h1>
                <p className="text-xs text-[#8C7B75] font-cinzel tracking-wider">KALABURAGI</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className="nav-link text-sm font-medium text-[#5C4033] hover:text-[#FF6A00]"
                  data-testid={`nav-${link.id}`}
                >
                  {link.label}
                </button>
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden bg-white border-t border-[#E6DCCA] shadow-lg" data-testid="mobile-menu">
            <div className="container-custom py-4">
              <nav className="flex flex-col gap-2">
                {navLinks.map((link) => (
                  <button
                    key={link.id}
                    onClick={() => scrollToSection(link.id)}
                    className="text-left py-3 px-4 text-[#5C4033] hover:bg-[#FFF5E6] rounded-lg transition-colors"
                  >
                    {link.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section id="hero" className="relative min-h-screen flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img 
            src={ASSETS.hero_goddess} 
            alt="Divine Goddess"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#FFFBF5]/30 via-[#FFFBF5]/60 to-[#FFFBF5]"></div>
        </div>

        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-[#D4AF37] rounded-full animate-particle"
              style={{
                left: `${20 + i * 15}%`,
                top: `${30 + (i % 3) * 20}%`,
                animationDelay: `${i * 0.5}s`,
                opacity: 0.4
              }}
            />
          ))}
        </div>

        <div className="container-custom relative z-10 pt-32 pb-20">
          <div className="max-w-3xl">
            <div className="section-badge mb-6 animate-fade-in-up" data-testid="hero-badge">
              <span className="font-cinzel">JAI MATA DI</span>
            </div>
            
            <h1 
              className="font-playfair text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-[#290909] leading-tight mb-6 animate-fade-in-up"
              style={{ animationDelay: "0.1s" }}
              data-testid="hero-title"
            >
              Bhavsar Kshatriya Samaj
            </h1>
            <p 
              className="text-lg sm:text-xl text-[#5C4033] mb-4 font-cinzel tracking-wider animate-fade-in-up"
              style={{ animationDelay: "0.2s" }}
            >
              KALABURAGI
            </p>
            <p 
              className="text-base sm:text-lg text-[#5C4033] mb-8 max-w-xl animate-fade-in-up"
              style={{ animationDelay: "0.3s" }}
              data-testid="hero-subtitle"
            >
              Faith, Tradition, and Community Unity — Experience the divine blessings and be part of our sacred heritage.
            </p>

            <div 
              className="flex flex-wrap gap-4 animate-fade-in-up"
              style={{ animationDelay: "0.4s" }}
            >
              <Button 
                className="btn-primary"
                onClick={() => scrollToSection('about')}
                data-testid="view-goddess-btn"
              >
                View Goddess
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button 
                className="btn-secondary"
                onClick={() => scrollToSection('events')}
                data-testid="events-btn"
              >
                <Calendar className="mr-2 h-4 w-4" />
                Upcoming Events
              </Button>
              <Button 
                variant="outline"
                className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white rounded-full px-6"
                onClick={() => scrollToSection('donations')}
                data-testid="donate-btn"
              >
                <Heart className="mr-2 h-4 w-4" />
                Donate Now
              </Button>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-[#D4AF37]" />
        </div>
      </section>

      {/* About Goddess Section */}
      <section id="about" className="section-padding bg-white" data-testid="about-section">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="relative animate-fade-in-up">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={ASSETS.temple_deity} 
                  alt="Divine Goddess"
                  className="w-full h-[400px] md:h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
              <div className="absolute -top-4 -right-4 w-full h-full border-2 border-[#D4AF37] rounded-2xl -z-10"></div>
            </div>

            <div className="stagger-children">
              <div className="section-badge mb-4">
                <span className="font-cinzel">ABOUT THE TEMPLE</span>
              </div>
              <h2 className="font-playfair text-3xl sm:text-4xl md:text-5xl font-semibold text-[#290909] mb-6" data-testid="about-title">
                Shri Higulambika Mata
              </h2>
              <p className="text-lg text-[#D4AF37] font-cinzel mb-4">Sacred Abode of Divine Mother</p>
              <div className="decorative-line mb-6"></div>
              <p className="text-[#5C4033] leading-relaxed mb-6">
                <strong className="text-[#290909]">Shri Higulambika Mata</strong>, the divine Mother Goddess, has her sacred origins in 
                the ancient Hinglaj Mata Temple located in Balochistan, Pakistan. This revered deity is one of the 51 Shakti Peethas, 
                where it is believed that the head (Brahmarandhra) of Goddess Sati fell. The Bhavsar Kshatriya community has been 
                devoted worshippers of Higulambika Mata for centuries.
              </p>
              <p className="text-[#5C4033] leading-relaxed mb-6">
                The Bhavsar Kshatriya Samaj Temple in Kalaburagi stands as a beacon of faith, bringing the divine blessings of 
                Higulambika Mata to our community in Karnataka. The temple is adorned with intricate silver work and traditional 
                decorations. Devotees from across Karnataka and neighboring states visit to seek blessings, participate in festivals, 
                and experience the divine presence of Mata Ji.
              </p>
              <div className="grid grid-cols-2 gap-4 mt-8">
                <div className="bg-[#FFF5E6] p-4 rounded-xl">
                  <div className="text-[#FF6A00] font-playfair text-2xl font-bold">100+</div>
                  <div className="text-sm text-[#5C4033]">Years of Heritage</div>
                </div>
                <div className="bg-[#FFF5E6] p-4 rounded-xl">
                  <div className="text-[#FF6A00] font-playfair text-2xl font-bold">1000+</div>
                  <div className="text-sm text-[#5C4033]">Devotee Families</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section id="events" className="section-padding bg-[#F9F5F0]" data-testid="events-section">
        <div className="container-custom">
          <div className="text-center mb-12 stagger-children">
            <div className="section-badge mb-4 mx-auto w-fit">
              <Calendar className="w-4 h-4" />
              <span className="font-cinzel">UPCOMING EVENTS</span>
            </div>
            <h2 className="font-playfair text-3xl sm:text-4xl md:text-5xl font-semibold text-[#290909] mb-4" data-testid="events-title">
              Join Our Sacred Celebrations
            </h2>
            <p className="text-[#5C4033] max-w-2xl mx-auto">
              Participate in our festivals, poojas, and community gatherings. Experience the joy of devotion together.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {EVENTS.map((event, index) => (
              <div 
                key={event.event_id} 
                className="event-card card-glass overflow-hidden animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`event-card-${index}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={event.image_url} 
                    alt={event.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-[#FF6A00] text-white text-xs font-medium px-3 py-1 rounded-full capitalize">
                      {event.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-playfair text-xl font-semibold text-[#290909] mb-3">{event.title}</h3>
                  <p className="text-[#5C4033] text-sm mb-4 line-clamp-2">{event.description}</p>
                  <div className="space-y-2 text-sm text-[#8C7B75]">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-[#D4AF37]" />
                      <span>{new Date(event.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                    </div>
                    {event.time && (
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-[#D4AF37]" />
                        <span>{event.time}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-[#D4AF37]" />
                      <span className="truncate">{event.location}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Instagram Feed Section */}
      <section id="instagram" className="section-padding bg-white" data-testid="instagram-section">
        <div className="container-custom">
          <div className="text-center mb-12 stagger-children">
            <div className="section-badge mb-4 mx-auto w-fit bg-gradient-to-r from-[#F58529]/10 via-[#DD2A7B]/10 to-[#8134AF]/10 border-[#DD2A7B]/20">
              <Instagram className="w-4 h-4 text-[#DD2A7B]" />
              <span className="font-cinzel text-[#DD2A7B]">FOLLOW US ON INSTAGRAM</span>
            </div>
            <h2 className="font-playfair text-3xl sm:text-4xl md:text-5xl font-semibold text-[#290909] mb-4" data-testid="instagram-title">
              @bks_klb
            </h2>
            <p className="text-[#5C4033] max-w-2xl mx-auto">
              Stay connected with our latest updates, behind-the-scenes moments, and community highlights on Instagram.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-[#F58529]/5 via-[#DD2A7B]/5 to-[#8134AF]/5 rounded-2xl p-8 border border-[#DD2A7B]/10">
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-4 bg-white rounded-2xl p-4 shadow-lg">
                  <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-[#DD2A7B]">
                    <img 
                      src={ASSETS.hero_goddess} 
                      alt="BKS KLB Instagram" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-[#290909]">bks_klb</h3>
                    <p className="text-sm text-[#8C7B75]">Bhavsar Kshatriya Samaj, Kalaburagi</p>
                  </div>
                  <a 
                    href="https://www.instagram.com/bks_klb/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-4 px-6 py-2 bg-gradient-to-r from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity"
                    data-testid="instagram-follow-btn"
                  >
                    Follow
                  </a>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                {[ASSETS.hero_goddess, ASSETS.temple_deity, ASSETS.festival_1, ASSETS.festival_2, ASSETS.procession, ASSETS.hero_goddess].map((img, index) => (
                  <a 
                    key={index}
                    href="https://www.instagram.com/bks_klb/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative aspect-square rounded-xl overflow-hidden group"
                    data-testid={`instagram-post-${index}`}
                  >
                    <img 
                      src={img} 
                      alt={`Instagram post ${index + 1}`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                      <Instagram className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                  </a>
                ))}
              </div>

              <div className="text-center">
                <a 
                  href="https://www.instagram.com/bks_klb/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white font-medium rounded-full hover:shadow-lg hover:shadow-[#DD2A7B]/30 transition-all"
                  data-testid="view-instagram-btn"
                >
                  <Instagram className="w-5 h-5" />
                  View All Posts on Instagram
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="section-padding bg-[#F9F5F0]" data-testid="gallery-section">
        <div className="container-custom">
          <div className="text-center mb-12 stagger-children">
            <div className="section-badge mb-4 mx-auto w-fit">
              <span className="font-cinzel">DIVINE GALLERY</span>
            </div>
            <h2 className="font-playfair text-3xl sm:text-4xl md:text-5xl font-semibold text-[#290909] mb-4" data-testid="gallery-title">
              Glimpses of Devotion
            </h2>
            <p className="text-[#5C4033] max-w-2xl mx-auto">
              Explore beautiful moments from our temple, festivals, and community gatherings.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3 mb-10" data-testid="gallery-filters">
            {["all", "goddess", "events", "community"].map((filter) => (
              <button
                key={filter}
                onClick={() => setSelectedGalleryFilter(filter)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedGalleryFilter === filter
                    ? "bg-[#FF6A00] text-white shadow-lg"
                    : "bg-white text-[#5C4033] hover:bg-[#FFF5E6]"
                }`}
                data-testid={`filter-${filter}`}
              >
                {filter.charAt(0).toUpperCase() + filter.slice(1)}
              </button>
            ))}
          </div>

          <div className="gallery-grid">
            {filteredGallery.map((image, index) => (
              <div 
                key={image.image_id} 
                className="gallery-item cursor-pointer animate-fade-in-up"
                style={{ animationDelay: `${index * 0.05}s` }}
                onClick={() => setLightboxImage(image)}
                data-testid={`gallery-item-${index}`}
              >
                <img src={image.image_url} alt={image.title} loading="lazy" />
                <div className="overlay flex items-end p-4">
                  <div className="text-white">
                    <h4 className="font-medium">{image.title}</h4>
                    <p className="text-sm opacity-80 capitalize">{image.category}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {lightboxImage && (
            <Dialog open={!!lightboxImage} onOpenChange={() => setLightboxImage(null)}>
              <DialogContent className="max-w-4xl p-0 bg-black/95 border-none">
                <div className="relative">
                  <img 
                    src={lightboxImage.image_url} 
                    alt={lightboxImage.title}
                    className="w-full h-auto max-h-[80vh] object-contain"
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                    <h3 className="text-white font-playfair text-xl">{lightboxImage.title}</h3>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </section>

      {/* Donations Section */}
      <section id="donations" className="section-padding bg-white" data-testid="donations-section">
        <div className="container-custom">
          <div className="text-center mb-12 stagger-children">
            <div className="section-badge mb-4 mx-auto w-fit">
              <Heart className="w-4 h-4" />
              <span className="font-cinzel">SUPPORT THE TEMPLE</span>
            </div>
            <h2 className="font-playfair text-3xl sm:text-4xl md:text-5xl font-semibold text-[#290909] mb-4" data-testid="donations-title">
              Contribute to Divine Cause
            </h2>
            <p className="text-[#5C4033] max-w-2xl mx-auto">
              Your generous donations help maintain the temple, organize festivals, and support community welfare activities.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="donation-card rounded-2xl p-8 md:p-12 relative overflow-hidden">
              <div className="relative z-10">
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <div className="flex-1 text-center md:text-left">
                    <div className="inline-flex items-center gap-2 bg-white/80 px-4 py-2 rounded-full mb-4">
                      <div className="w-6 h-6 bg-[#FF6A00] rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">U</span>
                      </div>
                      <span className="text-sm font-medium text-[#290909]">UPI Payment</span>
                    </div>
                    <h3 className="font-playfair text-2xl md:text-3xl font-semibold text-[#290909] mb-2">
                      Donate via UPI
                    </h3>
                    <p className="text-[#5C4033] mb-6">
                      Scan the QR code or use the UPI ID below to make your contribution.
                    </p>
                    
                    <div className="upi-card inline-block" data-testid="upi-card">
                      <p className="text-sm text-[#8C7B75] mb-1">UPI ID</p>
                      <p className="text-xl font-semibold text-[#290909] font-mono">9110278059@ybl</p>
                      <button 
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText("9110278059@ybl");
                            toast.success("UPI ID copied to clipboard!");
                          } catch (err) {
                            const textArea = document.createElement("textarea");
                            textArea.value = "9110278059@ybl";
                            document.body.appendChild(textArea);
                            textArea.select();
                            document.execCommand("copy");
                            document.body.removeChild(textArea);
                            toast.success("UPI ID copied to clipboard!");
                          }
                        }}
                        className="mt-3 text-sm text-[#FF6A00] hover:underline"
                        data-testid="copy-upi-btn"
                      >
                        Click to copy
                      </button>
                    </div>
                  </div>

                  <div className="flex-shrink-0">
                    <div className="w-48 h-48 bg-white rounded-2xl shadow-lg flex items-center justify-center p-2">
                      <div className="text-center">
                        <img 
                          src="/upi-qr.png" 
                          alt="UPI QR Code" 
                          className="w-40 h-40 object-contain"
                          data-testid="upi-qr-code"
                        />
                        <p className="text-xs text-[#8C7B75] mt-1">Scan to Pay</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-10 pt-8 border-t border-[#E6DCCA]/50">
                  <p className="text-sm text-[#8C7B75] text-center mb-4">Other ways to contribute</p>
                  <div className="flex flex-wrap justify-center gap-4">
                    <div className="bg-white/60 px-6 py-3 rounded-lg text-center">
                      <p className="text-sm font-medium text-[#290909]">Temple Visit</p>
                      <p className="text-xs text-[#8C7B75]">Donation Box</p>
                    </div>
                    <div className="bg-white/60 px-6 py-3 rounded-lg text-center">
                      <p className="text-sm font-medium text-[#290909]">Sponsor Pooja</p>
                      <p className="text-xs text-[#8C7B75]">Contact Temple</p>
                    </div>
                    <div className="bg-white/60 px-6 py-3 rounded-lg text-center">
                      <p className="text-sm font-medium text-[#290909]">Volunteer</p>
                      <p className="text-xs text-[#8C7B75]">Join Activities</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section id="community" className="section-padding bg-[#F9F5F0]" data-testid="community-section">
        <div className="container-custom">
          <div className="text-center mb-12 stagger-children">
            <div className="section-badge mb-4 mx-auto w-fit">
              <Users className="w-4 h-4" />
              <span className="font-cinzel">COMMUNITY</span>
            </div>
            <h2 className="font-playfair text-3xl sm:text-4xl md:text-5xl font-semibold text-[#290909] mb-4" data-testid="community-title">
              News & Announcements
            </h2>
            <p className="text-[#5C4033] max-w-2xl mx-auto">
              Stay updated with the latest news, announcements, and activities from our Samaj.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {COMMUNITY_POSTS.map((post, index) => (
              <div 
                key={post.post_id}
                className={`card-glass p-6 animate-fade-in-up ${post.is_pinned ? 'border-l-4 border-l-[#D4AF37]' : ''}`}
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`community-post-${index}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {post.is_pinned && (
                      <span className="bg-[#D4AF37] text-white text-xs px-2 py-1 rounded">Pinned</span>
                    )}
                    <span className={`text-xs px-3 py-1 rounded-full capitalize ${
                      post.post_type === 'announcement' ? 'bg-[#FF6A00]/10 text-[#FF6A00]' : 'bg-[#6A0D0D]/10 text-[#6A0D0D]'
                    }`}>
                      {post.post_type}
                    </span>
                  </div>
                  <Bell className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <h3 className="font-playfair text-xl font-semibold text-[#290909] mb-3">{post.title}</h3>
                <p className="text-[#5C4033] text-sm leading-relaxed">{post.content}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section-padding bg-white" data-testid="contact-section">
        <div className="container-custom">
          <div className="text-center mb-12 stagger-children">
            <div className="section-badge mb-4 mx-auto w-fit">
              <Mail className="w-4 h-4" />
              <span className="font-cinzel">GET IN TOUCH</span>
            </div>
            <h2 className="font-playfair text-3xl sm:text-4xl md:text-5xl font-semibold text-[#290909] mb-4" data-testid="contact-title">
              Contact Us
            </h2>
            <p className="text-[#5C4033] max-w-2xl mx-auto">
              Have questions or want to know more? Reach out to us.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div className="card-glass p-8 animate-fade-in-up">
              <h3 className="font-playfair text-2xl font-semibold text-[#290909] mb-6">Send us a Message</h3>
              <form onSubmit={handleContactSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-[#5C4033] mb-2">Name *</label>
                    <Input
                      type="text"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                      required
                      className="contact-input w-full"
                      placeholder="Your name"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#5C4033] mb-2">Email *</label>
                    <Input
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                      required
                      className="contact-input w-full"
                      placeholder="your@email.com"
                      data-testid="contact-email"
                    />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-[#5C4033] mb-2">Phone</label>
                    <Input
                      type="tel"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                      className="contact-input w-full"
                      placeholder="+91 9876543210"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#5C4033] mb-2">Subject *</label>
                    <Input
                      type="text"
                      value={contactForm.subject}
                      onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                      required
                      className="contact-input w-full"
                      placeholder="How can we help?"
                      data-testid="contact-subject"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#5C4033] mb-2">Message *</label>
                  <Textarea
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    required
                    className="contact-input w-full min-h-[120px]"
                    placeholder="Your message..."
                    data-testid="contact-message"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="btn-primary w-full"
                  data-testid="contact-submit"
                >
                  <Send className="mr-2 h-4 w-4" />
                  Send via WhatsApp
                </Button>
              </form>
            </div>

            <div className="space-y-8 animate-fade-in-up" style={{ animationDelay: "0.2s" }}>
              <div>
                <h3 className="font-playfair text-2xl font-semibold text-[#290909] mb-6">Temple Information</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#FF6A00]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[#FF6A00]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#290909]">Address</p>
                      <p className="text-[#5C4033]">Bhavsar Kshatriya Samaj Temple<br/>Bhavani Nagar, Maktampur<br/>Gulbarga (Kalaburagi), Karnataka, India</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#FF6A00]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-[#FF6A00]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#290909]">Temple Timings</p>
                      <p className="text-[#5C4033]">Daily: 6:00 AM - 9:00 PM<br/>Aarti: 7:00 AM & 7:00 PM</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#FF6A00]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-[#FF6A00]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#290909]">Phone</p>
                      <p className="text-[#5C4033]">+91 9110278059</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl overflow-hidden border border-[#E6DCCA] h-64">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3833.0!2d76.8!3d17.33!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc8c7e6e6e6e6e7%3A0x1234567890abcdef!2sMaktampur%2C%20Kalaburagi%2C%20Karnataka!5e0!3m2!1sen!2sin!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Temple Location - Bhavani Nagar, Maktampur, Gulbarga"
                ></iframe>
              </div>

              <div>
                <p className="font-medium text-[#290909] mb-4">Follow Us</p>
                <div className="flex gap-4">
                  <a 
                    href="https://www.facebook.com/people/Bhavsar-Kshatriya-Samaj-Kalaburagi/61566321253559/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-[#1877F2] rounded-xl flex items-center justify-center text-white hover:opacity-90 transition-opacity"
                    data-testid="social-facebook"
                  >
                    <Facebook className="w-5 h-5" />
                  </a>
                  <a 
                    href="https://www.instagram.com/bks_klb/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] rounded-xl flex items-center justify-center text-white hover:opacity-90 transition-opacity"
                    data-testid="social-instagram"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#290909] text-white py-12" data-testid="footer">
        <div className="container-custom">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-[#D4AF37]">
                  <img 
                    src={ASSETS.hero_goddess} 
                    alt="Temple Logo" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="font-playfair text-xl font-semibold">Bhavsar Kshatriya Samaj</h3>
                  <p className="text-sm text-[#D4AF37] font-cinzel">KALABURAGI</p>
                </div>
              </div>
              <p className="text-white/70 text-sm leading-relaxed max-w-md">
                A sacred community dedicated to preserving our traditions, serving the divine Mother, 
                and fostering unity among the Bhavsar Kshatriya community.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-[#D4AF37]">Quick Links</h4>
              <ul className="space-y-2 text-sm text-white/70">
                {navLinks.slice(0, 4).map((link) => (
                  <li key={link.id}>
                    <button 
                      onClick={() => scrollToSection(link.id)}
                      className="hover:text-white transition-colors"
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-[#D4AF37]">Contact</h4>
              <ul className="space-y-2 text-sm text-white/70">
                <li>Bhavani Nagar, Maktampur</li>
                <li>Gulbarga, Karnataka</li>
                <li>+91 9110278059</li>
                <li>UPI: 9110278059@ybl</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <p className="text-sm text-white/50">
                Bhavsar Kshatriya Samaj, Kalaburagi © 2026. All rights reserved.
              </p>
              <p className="text-xs text-white/40 mt-1">
                Designed By: <span className="text-[#D4AF37]">Viral Boostup</span>
              </p>
            </div>
            <div className="flex gap-4">
              <a 
                href="https://www.facebook.com/people/Bhavsar-Kshatriya-Samaj-Kalaburagi/61566321253559/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a 
                href="https://www.instagram.com/bks_klb/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <Instagram className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePageStatic;
